import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SITE, WEALTH_SERVICES, FINANCE_SERVICES, RISK_SERVICES, TESTIMONIALS } from '../data/siteData';
import { useScrollReveal } from '../hooks/useScrollReveal';
import home from '../assets/home.jpeg';
import india from '../assets/indias.jpeg';
import risk from '../assets/risk.jpeg';
import durgam from '../assets/durgam.jpeg';
import blog1 from '../assets/blog1.jpeg';
import blog2 from '../assets/blog2.jpeg';
import blog3 from '../assets/blog3.jpeg';
import finance from '../assets/finance.jpeg';
import Icon from '../components/Icon';
import charminar from '../assets/charminar.jpeg';
import narendra from '../assets/pro.png';
import { fetchAllPosts, LINKEDIN_URL } from '../data/blogData';
import art from '../assets/art.jpeg';
import './Home.css';

/* ── Images via Unsplash ── */
const IMG = {
  hero:       'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1400&q=80&auto=format&fit=crop',
  cityscape:  charminar,
  advisor:     india,
  meeting:     home,
  riskm:       risk,
  arto:        art,
  charts:     'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&q=80&auto=format&fit=crop',
  growth:     'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&q=80&auto=format&fit=crop',
  family:     finance,
  hyderabad:  'https://images.unsplash.com/photo-1562979314-bee7453e911c?w=900&q=80&auto=format&fit=crop',
};

// Define blog images at the top of your Home component

function ServiceCard({ service, delay }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div ref={ref} className={`sv-card${visible?' visible':''}`} style={{transitionDelay:delay}}>
      <div className="sv-icon"><Icon icon={service.icon} size={40} /></div>
      <div className="sv-name">{service.short||service.name}</div>
      <div className="sv-desc">{service.tagline||service.desc?.slice(0,80)+'…'}</div>
      {service.minInvestment && <div className="sv-min">Min: {service.minInvestment}</div>}
      <span className="sv-arrow">→</span>
      <div className="sv-bar"/>
    </div>
  );
}

function WhyCard({ icon, title, text, delay }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div ref={ref} className={`why-card${visible?' visible':''}`} style={{transitionDelay:delay}}>
      <div className="wc-icon">{icon}</div>
      <h4 className="wc-title">{title}</h4>
      <p className="wc-text">{text}</p>
    </div>
  );
}

function TestiCard({ t, delay }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div ref={ref} className={`testi-card${visible?' visible':''}`} style={{transitionDelay:delay}}>
      <div className="tc-stars">{'★'.repeat(t.rating)}</div>
      <blockquote className="tc-text">"{t.text}"</blockquote>
      <div className="tc-author">
        <div className="tc-avatar">{t.name[0]}</div>
        <div>
          <div className="tc-name">{t.name}</div>
          <div className="tc-role">{t.role}</div>
        </div>
      </div>
    </div>
  );
}

function CalculatorWidget() {
  const [activeCalc, setActiveCalc] = useState('sip');
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);

  const calcs = [
    {id:'sip',label:'SIP',icon:'📈',desc:'Systematic Investment Plan'},
    {id:'advsip',label:'Advanced SIP',icon:'🚀',desc:'SIP with Step-Up & Lumpsum'},
    {id:'emi',label:'EMI',icon:'🏦',desc:'Loan EMI Calculator'},
    {id:'fd',label:'FD',icon:'💰',desc:'Fixed Deposit'},
    {id:'rd',label:'RD',icon:'🔄',desc:'Recurring Deposit'},
    {id:'ppf',label:'PPF',icon:'🏛️',desc:'Public Provident Fund'},
    {id:'lumpsum',label:'Lumpsum',icon:'💎',desc:'One-time Investment'},
    {id:'swp',label:'SWP',icon:'💸',desc:'Systematic Withdrawal'},
    {id:'gst',label:'GST',icon:'📋',desc:'GST Calculator'},
    {id:'ci',label:'Compound',icon:'📊',desc:'Compound Interest'},
    {id:'si',label:'Simple',icon:'🔢',desc:'Simple Interest'},
    {id:'inflation',label:'Inflation',icon:'📉',desc:'Inflation Impact'},
    {id:'pnl',label:'P&L',icon:'📌',desc:'Profit & Loss'},
  ];

  const calcDefs = {
    sip: {
      fields:[{k:'monthly',l:'Monthly Investment (₹)',ph:'5000'},{k:'rate',l:'Expected Return (%/yr)',ph:'12'},{k:'years',l:'Time Period (Years)',ph:'10'}],
      calc:(v)=>{
        const p=parseFloat(v.monthly)||0, r=(parseFloat(v.rate)||12)/12/100, n=(parseFloat(v.years)||10)*12;
        const fv = p*((Math.pow(1+r,n)-1)/r)*(1+r);
        const invested = p*n;
        return {main:`₹${fmt(fv)}`,sub:`Invested: ₹${fmt(invested)} | Gains: ₹${fmt(fv-invested)}`,growth:Math.round(((fv-invested)/invested)*100)};
      }
    },
    advsip: {
      fields:[
        {k:'monthly',l:'Monthly SIP (₹)',ph:'5000'},
        {k:'stepup',l:'Annual Step-Up (%)',ph:'10'},
        {k:'lumpsum',l:'Lumpsum Amount (₹)',ph:'0'},
        {k:'rate',l:'Expected Return (%/yr)',ph:'12'},
        {k:'years',l:'Time Period (Years)',ph:'15'}
      ],
      calc:(v)=>{
        const p=parseFloat(v.monthly)||0;
        const su=(parseFloat(v.stepup)||0)/100;
        const lump=parseFloat(v.lumpsum)||0;
        const rate=(parseFloat(v.rate)||12)/12/100;
        const yrs=parseFloat(v.years)||15;
        let fv=lump*Math.pow(1+rate,yrs*12);
        let monthly=p, invested=lump;
        for(let y=0;y<yrs;y++){
          for(let m=0;m<12;m++){
            fv=(fv+monthly)*(1+rate);
          }
          invested+=monthly*12;
          monthly=monthly*(1+su);
        }
        return {main:`₹${fmt(fv)}`,sub:`Invested: ₹${fmt(invested)} | Gains: ₹${fmt(fv-invested)}`,growth:Math.round(((fv-invested)/invested)*100)};
      }
    },
    emi: {
      fields:[{k:'principal',l:'Loan Amount (₹)',ph:'1000000'},{k:'rate',l:'Interest Rate (%/yr)',ph:'8.5'},{k:'years',l:'Loan Tenure (Years)',ph:'20'}],
      calc:(v)=>{
        const p=parseFloat(v.principal)||0, r=(parseFloat(v.rate)||8.5)/12/100, n=(parseFloat(v.years)||20)*12;
        const emi = p*r*Math.pow(1+r,n)/(Math.pow(1+r,n)-1);
        const total = emi*n;
        return {main:`₹${fmt(emi)}/mo`,sub:`Total Payment: ₹${fmt(total)} | Interest: ₹${fmt(total-p)}`,growth:null};
      }
    },
    fd: {
      fields:[{k:'principal',l:'Principal Amount (₹)',ph:'100000'},{k:'rate',l:'Interest Rate (%/yr)',ph:'7'},{k:'years',l:'Tenure (Years)',ph:'5'}],
      calc:(v)=>{
        const p=parseFloat(v.principal)||0, r=parseFloat(v.rate)||7, n=parseFloat(v.years)||5;
        const maturity = p*Math.pow(1+r/400,4*n);
        return {main:`₹${fmt(maturity)}`,sub:`Interest Earned: ₹${fmt(maturity-p)}`,growth:Math.round(((maturity-p)/p)*100)};
      }
    },
    rd: {
      fields:[{k:'monthly',l:'Monthly Deposit (₹)',ph:'5000'},{k:'rate',l:'Interest Rate (%/yr)',ph:'6.5'},{k:'years',l:'Tenure (Years)',ph:'3'}],
      calc:(v)=>{
        const p=parseFloat(v.monthly)||0, r=parseFloat(v.rate)||6.5, n=parseFloat(v.years)*12||36;
        const maturity = p*n + p*n*(n+1)*(r/100)/(2*12);
        return {main:`₹${fmt(maturity)}`,sub:`Deposited: ₹${fmt(p*n)} | Interest: ₹${fmt(maturity-p*n)}`,growth:null};
      }
    },
    ppf: {
      fields:[{k:'yearly',l:'Yearly Investment (₹)',ph:'150000'},{k:'years',l:'Tenure (Years)',ph:'15'}],
      calc:(v)=>{
        const p=parseFloat(v.yearly)||0, n=parseFloat(v.years)||15, r=0.071;
        let bal=0; for(let i=0;i<n;i++) bal=(bal+p)*(1+r);
        return {main:`₹${fmt(bal)}`,sub:`Invested: ₹${fmt(p*n)} | Gains: ₹${fmt(bal-p*n)}`,growth:Math.round(((bal-p*n)/(p*n))*100)};
      }
    },
    lumpsum: {
      fields:[{k:'principal',l:'Investment Amount (₹)',ph:'100000'},{k:'rate',l:'Expected Return (%/yr)',ph:'12'},{k:'years',l:'Time Period (Years)',ph:'10'}],
      calc:(v)=>{
        const p=parseFloat(v.principal)||0, r=parseFloat(v.rate)||12, n=parseFloat(v.years)||10;
        const fv = p*Math.pow(1+r/100,n);
        return {main:`₹${fmt(fv)}`,sub:`Invested: ₹${fmt(p)} | Gains: ₹${fmt(fv-p)}`,growth:Math.round(((fv-p)/p)*100)};
      }
    },
    swp: {
      fields:[{k:'corpus',l:'Total Corpus (₹)',ph:'1000000'},{k:'monthly',l:'Monthly Withdrawal (₹)',ph:'8000'},{k:'rate',l:'Expected Return (%/yr)',ph:'8'}],
      calc:(v)=>{
        const c=parseFloat(v.corpus)||0, w=parseFloat(v.monthly)||0, r=(parseFloat(v.rate)||8)/12/100;
        const months = c>0&&w>0 ? Math.log(w/(w-c*r))/Math.log(1+r) : 0;
        const years = months/12;
        return {main:`${years.toFixed(1)} Years`,sub:`Total Withdrawals: ₹${fmt(w*months)} | Corpus: ₹${fmt(c)}`,growth:null};
      }
    },
    gst: {
      fields:[{k:'amount',l:'Amount (₹)',ph:'10000'},{k:'rate',l:'GST Rate (%)',ph:'18'}],
      calc:(v)=>{
        const a=parseFloat(v.amount)||0, r=parseFloat(v.rate)||18;
        const gst=a*r/100;
        return {main:`₹${fmt(a+gst)}`,sub:`GST: ₹${fmt(gst)} (${r}%) | Base: ₹${fmt(a)}`,growth:null};
      }
    },
    ci: {
      fields:[{k:'principal',l:'Principal (₹)',ph:'100000'},{k:'rate',l:'Rate (%/yr)',ph:'10'},{k:'years',l:'Years',ph:'5'}],
      calc:(v)=>{
        const p=parseFloat(v.principal)||0, r=parseFloat(v.rate)||10, n=parseFloat(v.years)||5;
        const fv=p*Math.pow(1+r/100,n);
        return {main:`₹${fmt(fv)}`,sub:`Interest: ₹${fmt(fv-p)} | Growth: ${Math.round(((fv-p)/p)*100)}%`,growth:Math.round(((fv-p)/p)*100)};
      }
    },
    si: {
      fields:[{k:'principal',l:'Principal (₹)',ph:'100000'},{k:'rate',l:'Rate (%/yr)',ph:'10'},{k:'years',l:'Years',ph:'5'}],
      calc:(v)=>{
        const p=parseFloat(v.principal)||0, r=parseFloat(v.rate)||10, n=parseFloat(v.years)||5;
        const interest=p*r*n/100;
        return {main:`₹${fmt(p+interest)}`,sub:`Simple Interest: ₹${fmt(interest)}`,growth:null};
      }
    },
    inflation: {
      fields:[{k:'amount',l:'Current Value (₹)',ph:'100000'},{k:'rate',l:'Inflation Rate (%)',ph:'6'},{k:'years',l:'Years',ph:'10'}],
      calc:(v)=>{
        const a=parseFloat(v.amount)||0, r=parseFloat(v.rate)||6, n=parseFloat(v.years)||10;
        const future=a*Math.pow(1+r/100,n);
        return {main:`₹${fmt(future)}`,sub:`You'll need ₹${fmt(future)} to match today's ₹${fmt(a)}`,growth:null};
      }
    },
    pnl: {
      fields:[{k:'buy',l:'Buy Price (₹)',ph:'100'},{k:'qty',l:'Quantity',ph:'100'},{k:'sell',l:'Sell Price (₹)',ph:'120'}],
      calc:(v)=>{
        const b=parseFloat(v.buy)||0, q=parseFloat(v.qty)||0, s=parseFloat(v.sell)||0;
        const pnl=(s-b)*q; const pct=b>0?((s-b)/b*100):0;
        return {main:`${pnl>=0?'Profit':'Loss'}: ₹${fmt(Math.abs(pnl))}`,sub:`Buy: ₹${fmt(b*q)} | Sell: ₹${fmt(s*q)} | ${pct.toFixed(2)}%`,growth:pnl>=0?pct:null};
      }
    }
  };

  function fmt(n){
    if(n>=10000000) return (n/10000000).toFixed(2)+' Cr';
    if(n>=100000) return (n/100000).toFixed(2)+' L';
    if(n>=1000) return (n/1000).toFixed(1)+' K';
    return Math.round(n).toLocaleString('en-IN');
  }

  const def = calcDefs[activeCalc];

  function handleCalc() {
    if(def) setResult(def.calc(inputs));
  }

  return (
    <div className="calc-widget">
      <div className="calc-tabs">
        {calcs.map(c=>(
          <button key={c.id} className={`calc-tab${activeCalc===c.id?' active':''}`} onClick={()=>{setActiveCalc(c.id);setResult(null);setInputs({});}}>
            <span className="ct-icon"><Icon icon={c.icon} size={22} /></span>
            <span className="ct-label">{c.label}</span>
          </button>
        ))}
      </div>
      {def && (
        <div className="calc-body">
          <div className="calc-inputs">
            <div className="calc-desc">{calcs.find(c=>c.id===activeCalc)?.desc}</div>
            {def.fields.map(f=>(
              <div key={f.k} className="calc-field">
                <label className="calc-label">{f.l}</label>
                <input
                  className="calc-input"
                  type="number"
                  placeholder={f.ph}
                  value={inputs[f.k]||''}
                  onChange={e=>setInputs({...inputs,[f.k]:e.target.value})}
                />
              </div>
            ))}
            <button className="calc-btn" onClick={handleCalc}>Calculate →</button>
          </div>
          {result && (
            <div className="calc-result">
              <div className="cr-label">Result</div>
              <div className="cr-main">{result.main}</div>
              <div className="cr-sub">{result.sub}</div>
              {result.growth!=null && (
                <div className="cr-growth">
                  <div className="cr-growth-bar"><div className="cr-growth-fill" style={{width:`${Math.min(result.growth,100)}%`}}/></div>
                  <div className="cr-growth-label">{result.growth}% returns</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { ref: s1Ref, visible: s1Vis } = useScrollReveal();
  const { ref: s2Ref, visible: s2Vis } = useScrollReveal();
  const { ref: s3Ref, visible: s3Vis } = useScrollReveal();
  const { ref: s4Ref, visible: s4Vis } = useScrollReveal();
  const { ref: s5Ref, visible: s5Vis } = useScrollReveal();

  // ─── BLOG STATE ───
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
    const BLOG_IMAGES = [
    'https://media.licdn.com/dms/image/v2/D5622AQFe6nl2N4iPag/feedshare-shrink_800/B56Z7Af6WdJoAc-/0/1781346051765?e=1783555200&v=beta&t=6v5MvHxT5QD9BRHwnVKEvSIYmjSzQlw0QFqBpfFKe3E',
    'https://media.licdn.com/dms/image/v2/D5622AQGB5_kBzKVqWA/feedshare-shrink_800/B56Z7ANDPNJQAc-/0/1781341107429?e=1783555200&v=beta&t=DB6_NERp6aoe5isFJyHo0XXlumgx7yqHVokD6iIS5qk',
    'https://media.licdn.com/dms/image/v2/D5622AQEnU89Nh3INdw/feedshare-shrink_800/B56Z6_nQQCG4Ac-/0/1781331199274?e=1783555200&v=beta&t=OF7O4Wj2MX5wYCaXcipg_UCzhRLLFzfToGG-R_ihOWc'
  ];

  // ─── FETCH BLOG POSTS ───
  useEffect(() => {
    let alive = true;
    fetchAllPosts().then(posts => {
      if (alive) {
        setBlogPosts(posts.slice(0, 3)); // Get only first 3 blogs
        setLoadingBlogs(false);
      }
    });
    return () => { alive = false; };
  }, []);

  return (
    <div className="home">

      {/* ══════════════════════════════════ HERO ══════════════════════════════════ */}
      <section className="hero">
        {/* Full-bleed photo background */}
        <div className="hero-img-wrap">
          <img src={IMG.hero} alt="Financial growth" className="hero-bg-img" />
          <div className="hero-overlay"/>
        </div>

        <div className="hero-content">
          <div className="hero-left">
            <div className="hero-badge">
              <span className="badge-dot"/>
              India's Trusted Wealth Partner
            </div>

            <h1 className="hero-h1">
              <span className="hl-art">The Art of Growing Wealth</span>
              <span className="hl-2">Build Your</span>
              <span className="hl-3">Financial Legacy.</span>
            </h1>

            <p className="hero-sub">
              Expert-led <strong>Wealth Management, </strong> <strong>Risk Management </strong> & <strong>Financial Management</strong> —
              PMS, Mutual Funds, Stock Market, Insurance & Loans Etc.. One destination for every financial goal.
            </p>

            <div className="hero-actions">
              <Link to="/contact" className="btn-primary">Start Investing →</Link>
              {/* <Link to="/services" className="btn-ghost light">Explore Services</Link> */}
            </div>

            <div className="hero-stats">
              {SITE.stats.map((s,i) => (
                <div key={i} className="hstat">
                  <span className="hstat-num">{s.num}</span>
                  <span className="hstat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — floating info cards */}
          <div className="hero-right-img">
            <div className="hero-right-img-wrap">
              <img
                src={IMG.meeting}
                alt="G.M. Narendra — Financial Advisor"
                className="hero-advisor-img"
              />
              <div className="hero-img-badge">
                <span className="hib-icon">🏆</span>
                <div>
                  <div className="hib-title">25+ Years</div>
                  <div className="hib-sub">Advisory Excellence</div>
                </div>
              </div>
              <div className="hero-img-badge2">
                <span className="hib-icon">👥</span>
                <div>
                  <div className="hib-title">1000+ Clients</div>
                  <div className="hib-sub">Across India & Abroad</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="hero-scroll-cue">
          <div className="scroll-dot"/>
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* ══ TICKER ══ */}
      <div className="ticker">
        <div className="ticker-track">
          {[...WEALTH_SERVICES,...FINANCE_SERVICES,...WEALTH_SERVICES,...FINANCE_SERVICES].map((s,i)=>(
            <span key={i} className="ticker-item">
              <Icon icon={s.icon} size={18} /> {s.short||s.name} 
              {/* <span className="ticker-sep">◆</span> */}
            </span>
          ))}
        </div>
      </div>

      {/* ══ ABOUT STRIP — image + text ══ */}
      <section className="about-strip">
        <div className="as-img-col">
          <img src={IMG.advisor} alt="Financial advisor" className="as-img"/>
          {/* <div className="as-img-badge">
            <div className="asb-num">25+</div>
            <div className="asb-label">Years of Excellence</div>
          </div> */}
        </div>
        <div ref={s1Ref} className="as-text-col">
          <div className={`section-label${s1Vis?' reveal-visible':' reveal'}`}>Who We Are</div>
          <h2 className={`section-title${s1Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.1s'}}>
            India's Most Trusted<br/><span className="green">Wealth & Finance Partner</span>
          </h2>
          <div className="green-divider" style={{margin:'20px 0'}}/>
          <p className={`section-sub${s1Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.2s'}}>
            Founded in <strong>2000</strong> by <strong>G.M. Narendra</strong> (M.Com · MBA · PGDCS), LMS Marketing & Services has guided 1000+ families across Hyderabad, India and abroad toward financial freedom — with integrity, research, and a client-first approach.
          </p>
          <div className={`as-pills${s1Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.3s'}}>
            {['SEBI Compliant','Goal-Based Planning','1000+ Clients','₹100Cr+ Managed'].map(p=>(
              <span key={p} className="as-pill">{p}</span>
            ))}
          </div>
          <div className={`as-actions${s1Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.4s'}}>
            <Link to="/about" className="btn-primary">Meet G.M. Narendra →</Link>
            <a href={`tel:${SITE.contact.phone}`} className="btn-ghost">📞 {SITE.contact.phone}</a>
          </div>
        </div>
      </section>

      {/* ══ SERVICES ══ */}
      <section className="services-sec">
        <div ref={s2Ref} className="services-header container">
          <div>
            <div className={`section-label${s2Vis?' reveal-visible':' reveal'}`}>Complete Ecosystem</div>
            <h2 className={`section-title${s2Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.1s'}}>
              Every Instrument.<br/><span className="green">Every Strategy.</span>
            </h2>
          </div>
          <p className={`section-sub${s2Vis?' reveal-visible':' reveal'}`} style={{maxWidth:380,transitionDelay:'0.2s'}}>
            Professionally managed across all major asset classes — tailored to your goals.
          </p>
        </div>

        {/* Wealth banner */}
        <div className="sv-banner">
          <img src={IMG.charts} alt="Charts" className="sv-banner-img"/>
          <div className="sv-banner-overlay"/>
          <div className="sv-banner-text">
            <span className="sv-banner-label">Wealth Management</span>
            <h3 className="sv-banner-title">Grow & Multiply Your Capital</h3>
          </div>
        </div>
        <div className="container">
          <div className="sv-grid sv-grid-5">
            {WEALTH_SERVICES.slice(0,5).map((s,i)=>(
              <Link to={`/service/wealth/${s.id}`} key={s.id} style={{textDecoration:'none'}}>
                <ServiceCard service={s} delay={`${i*0.07}s`}/>
              </Link>
            ))}
          </div>
          <div className="sv-grid sv-grid-5" style={{marginTop:16}}>
            {WEALTH_SERVICES.slice(5).map((s,i)=>(
              <Link to={`/service/wealth/${s.id}`} key={s.id} style={{textDecoration:'none'}}>
                <ServiceCard service={s} delay={`${i*0.07}s`}/>
              </Link>
            ))}
          </div>
        </div>

        
        {/* Risk Management banner */}
        <div className="sv-banner" style={{marginTop:64}}>
          <img src={IMG.riskm} alt="Risk protection" className="sv-banner-img"/>
          <div className="sv-banner-overlay" style={{background:'linear-gradient(90deg, rgba(14,80,50,0.88), rgba(10,50,30,0.45))'}}/>
          <div className="sv-banner-text">
            <span className="sv-banner-label" style={{background:'rgba(255,255,255,0.2)'}}>Risk Management</span>
            <h3 className="sv-banner-title">Protect What Matters Most</h3>
          </div>
        </div>
        <div className="container">
          <div className="sv-grid sv-grid-3">
            {RISK_SERVICES.map((s,i)=>(
              <Link to={`/service/risk/${s.id}`} key={s.id} style={{textDecoration:'none'}}>
                <ServiceCard service={s} delay={`${i*0.08}s`}/>
              </Link>
            ))}
          </div>
        </div>

        {/* Finance banner */}
        <div className="sv-banner" style={{marginTop:64}}>
          <img src={IMG.family} alt="Family protection" className="sv-banner-img"/>
          <div className="sv-banner-overlay" style={{background:'linear-gradient(90deg, rgba(192,32,31,0.85), rgba(139,21,21,0.4))'}}/>
          <div className="sv-banner-text">
            <span className="sv-banner-label" style={{background:'rgba(255,255,255,0.2)'}}>Finance Management</span>
            <h3 className="sv-banner-title">Protect & Fund Your Future</h3>
          </div>
        </div>
        <div className="container">
          <div className="sv-grid sv-grid-3">
            {FINANCE_SERVICES.map((s,i)=>(
              <Link to={`/service/finance/${s.id}`} key={s.id} style={{textDecoration:'none'}}>
                <ServiceCard service={s} delay={`${i*0.08}s`}/>
              </Link>
            ))}
          </div>
        </div>

        {/* <div className="container" style={{textAlign:'center',marginTop:48}}>
          <Link to="/services" className="btn-green-outline">View All Services →</Link>
        </div> */}
      </section>

      {/* ══ IMAGE SPLIT — WHY LMS ══ */}
      <section className="why-sec">
        <div className="why-img-half">
          <img src={IMG.arto} alt="Client meeting" className="why-img"/>
          {/* <div className="why-img-card">
            <div className="wic-icon">🏆</div>
            <div className="wic-text">"Best advisory relationship — trusted by 1000+ Indian families"</div>
          </div> */}
        </div>
        <div ref={s3Ref} className="why-text-half">
          <div className={`section-label${s3Vis?' reveal-visible':' reveal'}`}>Why Choose LMS</div>
          <h2 className={`section-title${s3Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.1s'}}>
            The Art of Growing Wealth —<br/><span className="green">Believe in the Best Service</span>
          </h2>
          <div className="green-divider" style={{margin:'18px 0 28px'}}/>
          <div className="why-cards-list">
            {[
              {icon:'🎯',title:'Goal-Based Planning',text:'Strategies built around your life milestones — retirement, home purchase, education.'},
              {icon:'🔍',title:'Research-Driven Advice',text:'Every recommendation backed by deep fundamental and technical analysis.'},
              {icon:'🤝',title:'Personal Relationship',text:'A real dedicated advisor who knows your name and your financial story.'},
              {icon:'🔐',title:'SEBI Compliant',text:'All investments held directly in your name. Regulated, transparent, fully compliant.'},
            ].map((w,i)=>(
              <div key={i} className={`why-row${s3Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:`${0.2+i*0.1}s`}}>
                <div className="wr-icon"><Icon icon={w.icon} size={40} /></div>
                <div>
                  <div className="wr-title">{w.title}</div>
                  <div className="wr-text">{w.text}</div>
                </div>
              </div>
            ))}
          </div>
          <div className={`${s3Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.7s',marginTop:32}}>
            <Link to="/about" className="btn-primary">Our Story →</Link>
          </div>
        </div>
      </section>

      {/* ══ STATS BANNER ══ */}
      <section className="stats-banner">
        <img src={durgam} alt="Hyderabad" className="stats-bg-img"/>
        <div className="stats-overlay"/>
        <div className="stats-content container">
          <div className="stats-tagline">Trusted across Hyderabad, India & Abroad — Since 2000</div>
          <div className="stats-grid">
            {SITE.stats.map((s,i)=>(
              <div key={i} className="stat-box">
                <div className="sb-num">{s.num}</div>
                <div className="sb-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROCESS ══ */}
      <section className="process-sec">
        <div ref={s4Ref} className="container">
          <div className="process-header" style={{textAlign:'center',marginBottom:64}}>
            <div className={`section-label${s4Vis?' reveal-visible':' reveal'}`} style={{justifyContent:'center'}}>How It Works</div>
            <h2 className={`section-title${s4Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.1s'}}>
              Your Path to <span className="green">Financial Freedom</span>
            </h2>
          </div>
          <div className="process-grid">
            {[
              {num:'01',icon:'📞',title:'Discovery Call',text:'Free 30-min consultation to understand your goals, risk profile & current situation.'},
              {num:'02',icon:'📋',title:'Custom Strategy',text:'We craft a tailored plan spanning investments, insurance & loan solutions.'},
              {num:'03',icon:'⚡',title:'Execution',text:'Seamless KYC, account setup & capital deployment across chosen instruments.'},
              {num:'04',icon:'📊',title:'Monitor & Grow',text:'Quarterly reviews, proactive rebalancing & ongoing advisory.'},
            ].map((s,i)=>{
              const {ref,visible}=useScrollReveal();
              return (
                <div key={i} ref={ref} className={`proc-card${visible?' visible':''}`} style={{transitionDelay:`${i*0.13}s`}}>
                  <div className="pc-num">{s.num}</div>
                  <div className="pc-icon"><Icon icon={s.icon} size={40} /></div>
                  <h4 className="pc-title">{s.title}</h4>
                  <p className="pc-text">{s.text}</p>
                  {i<3 && <div className="pc-arrow">→</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      {/* <section className="testi-sec">
        <div className="testi-img-bg">
          <img src={IMG.growth} alt="Growth" className="testi-bg-img"/>
          <div className="testi-bg-overlay"/>
        </div>
        <div ref={s5Ref} className="container testi-inner">
          <div className="testi-header">
            <div className={`section-label light${s5Vis?' reveal-visible':' reveal'}`}>Client Stories</div>
            <h2 className={`section-title light${s5Vis?' reveal-visible':' reveal'}`} style={{transitionDelay:'0.1s'}}>
              Trusted by <span style={{color:'#7DE48A'}}>500+ Families</span>
            </h2>
          </div>
          <div className="testi-grid">
            {TESTIMONIALS.map((t,i)=>(
              <TestiCard key={i} t={t} delay={`${i*0.1}s`}/>
            ))}
          </div>
        </div>
      </section> */}

      {/* ══ ADVISOR STRIP ══ */}
      <section className="advisor-strip">
        <div className="adv-img-col">
          <img src={IMG.cityscape} alt="Hyderabad cityscape" className="adv-img"/>
          <div className="adv-img-overlay"/>
          <div className="adv-img-text">
            <div className="adv-city">Secunderabad · Hyderabad</div>
            <div className="adv-addr">{SITE.contact.address}</div>
          </div>
        </div>
        <div className="adv-text-col">
            <img 
    src={narendra} 
    alt="G.M. Narendra" 
    className="adv-avatar" 
    style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover' }}
  />
          <h3 className="adv-name">G.M. Narendra</h3>
          <div className="adv-creds">M.Com · MBA · PGDCS · Management Consultant & Founder Of LMS Marketing Services</div>
          <p className="adv-desc">
            With 25+ years of hands-on market experience, G.M. Narendra brings a holistic approach to wealth management and financial planning for families across Hyderabad, India and abroad — with integrity, research, and a client-first approach since 2000.
          </p>
          <div className="adv-actions">
            <a href={`tel:${SITE.contact.phone}`} className="btn-primary">📞 {SITE.contact.phone}</a>
            <Link to="/about" className="btn-primary">Know More →</Link>
          </div>
        </div>
      </section>

{/* ══ BLOG SECTION ══ */}
<section className="blog-sec">
  <div className="container">
    <div style={{ textAlign: 'center', marginBottom: 48 }}>
      <div className="section-label" style={{ justifyContent: 'center' }}>Insights & Tips</div>
      <h2 className="section-title" style={{ textAlign: 'center' }}>
        Financial <span className="green">Blog</span>
      </h2>
    </div>
    
    {loadingBlogs ? (
      <p style={{ textAlign: 'center', color: '#888', padding: '40px 0' }}>Loading articles…</p>
    ) : blogPosts.length === 0 ? (
      <p style={{ textAlign: 'center', color: '#888', padding: '40px 0' }}>No blog posts available</p>
    ) : (
      <>
        <div className="blog-grid">
          {blogPosts.map((post, index) => {
            // Force use LinkedIn images - IGNORE any image from the post data
            const BLOG_IMAGES = [blog1, blog2, blog3];
            
            // FORCE use LinkedIn image - ignore post.image or post.img
            const imageSrc = BLOG_IMAGES[index] || IMG.growth;
            
            return (
              <div key={post.id} className="blog-card">
                <div className="bc-img-wrap">
                  <img 
                    src={imageSrc} 
                    alt={post.title} 
                    className="bc-img"
                    onError={(e) => {
                      e.target.src = IMG.growth;
                    }}
                  />
                </div>
                <div className="bc-body">
                  <div className="bc-meta">{post.date} · {post.author}</div>
                  <h4 className="bc-title">{post.title}</h4>
                  <p className="bc-excerpt" style={{ color: '#666', fontSize: '0.95rem', margin: '8px 0 12px' }}>
                    {post.excerpt?.substring(0, 100)}...
                  </p>
                  <a 
                    href={post.linkedinUrl || post.url || LINKEDIN_URL} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bc-link"
                  >
                    Read More →
                  </a>
                </div>
              </div>
            );
          })}
        </div>
        
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <a
            href="https://www.linkedin.com/in/narendragm03?utm_source=share_via&utm_content=profile&utm_medium=member_ios"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-green-outline"
          >
            View More on Linkedin →
          </a>
        </div>
      </>
    )}
  </div>
</section>

      {/* ══ YOUTUBE VIDEOS ══ */}
      <section className="yt-section">
        <div className="container">
          <div className="yt-header">
            <div className="section-label" style={{ justifyContent: 'center' }}>
              Watch &amp; Learn
            </div>

            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Financial Insights from <span className="green">G.M. Narendra</span>
            </h2>

            <p className="yt-sub">
              Expert advice, market updates and investment tips — straight from our Management Consultant.
            </p>
          </div>

          <div className="yt-grid">
            <div className="yt-card">
              <div className="yt-embed-wrap">
                <iframe
                  src="https://www.youtube.com/embed/EXxBkLnrGIE?si=ldHwspqr4IpduIfU"
                  title="LMS Video 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="yt-iframe"
                />
              </div>

              <div className="yt-card-body">
                <div className="yt-tag">Wealth Management</div>
                <div className="yt-title">
                  Smart Investment Strategies for 2025
                </div>
              </div>
            </div>

            <div className="yt-card">
              <div className="yt-embed-wrap">
                <iframe
                  src="https://www.youtube.com/embed/Yg9-o9VsVdo?si=PN_fJ_i1wgh5QJ_C"
                  title="LMS Video 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="yt-iframe"
                />
              </div>

              <div className="yt-card-body">
                <div className="yt-tag">Financial Planning</div>
                <div className="yt-title">
                  How to Secure Your Financial Future
                </div>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <a
              href="https://www.youtube.com/@earnmoneycreatewealth03"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-green-outline"
            >
              ▶ View More on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* ══ FINANCIAL CALCULATORS ══ */}
      <section className="calc-sec">
        <div className="container">
          <div style={{textAlign:'center',marginBottom:56}}>
            <div className="section-label" style={{justifyContent:'center'}}>Free Tools</div>
            <h2 className="section-title" style={{textAlign:'center'}}>
              Financial <span className="green">Calculators</span>
            </h2>
            <p className="section-sub" style={{textAlign:'center',maxWidth:560,margin:'12px auto 0'}}>
              Plan smarter with our suite of Indian financial calculators — EMI, SIP, FD, GST & more.
            </p>
          </div>
          <CalculatorWidget />
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section className="final-cta">
        <img src={IMG.charts} alt="" className="fcta-bg"/>
        <div className="fcta-overlay"/>
        <div className="fcta-inner container">
          <div className="fcta-label">Start Today — It's Free</div>
          <h2 className="fcta-title">
            Your Wealth Journey Begins<br/>with <span style={{color:'#7DE48A'}}>One Conversation</span>
          </h2>
          <p className="fcta-sub">
            Whether starting fresh or optimising an existing portfolio, we have the expertise to take you further.
          </p>
          <div className="fcta-actions">
            <a href={`tel:${SITE.contact.phone}`} className="btn-primary">📞 Call — {SITE.contact.phone}</a>
            <a href={`mailto:${SITE.contact.email}`} className="btn-primary">✉ Send Email</a>
            <Link
              to="/schedule-meeting"
              className="btn-primary"
              style={{
                borderColor: "rgba(255,255,255,0.4)",
                color: "#fff"
              }}
            >
              Schedule Meeting
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}